﻿import React from 'react';
import {
  CollapsableCardBodyContainer,
  CollapsableCardClickableContainer,
  CollapsableCardCollapseContainer,
  CollapsableCardContainer,
  CollapsableCardExpandIconButton,
  CollapsableCardHeaderActionsContainer,
  CollapsableCardHeaderContainer,
  CollapsableCardHeaderItem,
  CollapsableCardBodyItem,
} from "../../cardComponents";
import {LocationAllReportingVO} from "../../../types/model/valueObject/LocationAllReportingVO";
import {formatCurrencyValue} from "../../../util/PaymentUtils";
import BooleanUtils from "../../../util/BooleanUtils";
import {SubscriptionType} from "../../constants/booking-transaction-constants";
import { LocationReasonDeactivated } from "../../../types/model/status/LocationReasonDeactivated";

type AdminLocationsCardListItemProps = {
  item: LocationAllReportingVO;
  expanded?: boolean;
  onToggleExpand: () => void;
}

function getReasonDeactivated(item: LocationAllReportingVO): string | undefined {
  if (item.status === 'Deactivated' && item.reasonDeactivated) {
    return LocationReasonDeactivated[item.reasonDeactivated];
  }
  return undefined;
}

function getReasonDeactivatedNote(item: LocationAllReportingVO): string | undefined {
  return (item.status === 'Deactivated' && item.reasonDeactivatedNote) ? item.reasonDeactivatedNote : undefined;
}

const AdminLocationsCardListItem = ({ item, expanded, onToggleExpand }: AdminLocationsCardListItemProps) => {
  return <CollapsableCardContainer expanded={expanded}>
    <CollapsableCardClickableContainer onClick={onToggleExpand}>
      <CollapsableCardHeaderContainer>
        <CollapsableCardHeaderItem value={item?.locationName} label={'Location Name'} />
        <CollapsableCardHeaderItem value={item?.companyName} label={'Company Name'} />
        <CollapsableCardHeaderItem value={item?.managingEmail} label={'Managing Email'} />
        <CollapsableCardHeaderItem value={item?.phoneNumber} label={'Phone Number'} />
        <CollapsableCardHeaderItem value={item?.noBookings ?? 0} label={'# Of Bookings'} />
        <CollapsableCardHeaderItem value={item?.noLocations ?? 0} label={'# Of Locations'} />
        <CollapsableCardHeaderActionsContainer>
          <CollapsableCardExpandIconButton expanded={expanded ?? false} />
        </CollapsableCardHeaderActionsContainer>
      </CollapsableCardHeaderContainer>
    </CollapsableCardClickableContainer>
    <CollapsableCardCollapseContainer in={expanded}>
      <CollapsableCardBodyContainer>
        <CollapsableCardBodyItem value={item?.ssPercentage ?? 0} label={'Secur Space %'} />
        <CollapsableCardBodyItem value={BooleanUtils.convertToYesNo(item?.managedLocation)} label={'Instant Approval Location'} />
        <CollapsableCardBodyItem value={item?.managedSpaces ?? 0} label={'Total Instant Approval Spaces'} />
        <CollapsableCardBodyItem value={item?.addressLine1 ?? ''} label={'Address Line 1'} />
        <CollapsableCardBodyItem value={item?.addressLine2 ?? ''} label={'Address Line 2'} />
        <CollapsableCardBodyItem value={item?.city ?? ''} label={'City'} />
        <CollapsableCardBodyItem value={item?.state ?? ''} label={'State'} />
        <CollapsableCardBodyItem value={item?.zip ?? ''} label={'Zip'} />
        <CollapsableCardBodyItem value={BooleanUtils.convertToYesNo(item?.listingImageFileName)} label={'Location Image?'} />
        <CollapsableCardBodyItem value={item?.galleryImages ?? 0} label={'# Gallery Images'} />
        <CollapsableCardBodyItem value={item?.locationDescription ?? ''} label={'Location Description'} />
        <CollapsableCardBodyItem value={item?.facilityInstructions ?? ''} label={'Facility Instructions'} />
        <CollapsableCardBodyItem value={item?.locationEquipmentTypes ?? ''} label={'Equipment Types'} />
        <CollapsableCardBodyItem value={item?.locationFeatures ?? ''} label={'Features'} />
        <CollapsableCardBodyItem value={BooleanUtils.convertToYesNo(item?.allowsDailyBookings)} label={'Allows Daily Bookings'} />
        <CollapsableCardBodyItem value={formatCurrencyValue(item?.pricePerDay)} label={'Price Per Day'} />
        <CollapsableCardBodyItem value={formatCurrencyValue(item?.pricePerWeek)} label={'Price Per Week'} />
        <CollapsableCardBodyItem value={formatCurrencyValue(item?.pricePerMonth)} label={'Price Per Month'} />
        <CollapsableCardBodyItem value={item?.totalNumberOfSpaces} label={'Total Number Of Spaces'} />
        <CollapsableCardBodyItem value={item?.minNumberOfSpaces} label={'Min Number Of Spaces'} />
        <CollapsableCardBodyItem value={BooleanUtils.convertToYesNo(item?.reservationAgreement)} label={'Has Reservation Agreement'} />
        <CollapsableCardBodyItem value={item?.createdOn ?? ''} label={'Created On'} />
        <CollapsableCardBodyItem value={BooleanUtils.convertToYesNo(item?.accountActivated)} label={'Account Activated'} />
        <CollapsableCardBodyItem value={BooleanUtils.convertToYesNo(item?.isVisible)} label={'Visible In Search'} />
        <CollapsableCardBodyItem value={SubscriptionType[item.subscriptionType].label} label={'Subscription Type'} />
        <CollapsableCardBodyItem value={item?.status ?? ''} label={'Status'} />
        <CollapsableCardBodyItem value={getReasonDeactivated(item)} label={'Reason Deactivated'} />
        <CollapsableCardBodyItem value={getReasonDeactivatedNote(item)} label={'Reason Deactivated Note'} />
      </CollapsableCardBodyContainer>
    </CollapsableCardCollapseContainer>
  </CollapsableCardContainer>
}

export { AdminLocationsCardListItem };
